import { Box, Button, Checkbox, IconButton, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, TextField } from "@mui/material";
import { ModelRenderer } from "../ModelRenderer";
import { PropValueLine } from "../common/PropValueLine";
import { useEffect, useState } from "react";
import * as THREE from "three";
import { Model, ModelPart } from "../../models/Model";
import { MLEvents } from "../common/ModelLoader";
import { Vec3Editor } from "../common/Vec3Editor";
import { MREvents } from "../MREvents";
import { Delete } from "@mui/icons-material";
import { copyVec3 } from "../../visuals/visual-utils";
import { FlexContainer } from "../../components/FlexContainer";
import { Copy } from "lucide-react";


export function PartsPanel({ mr }: { mr: ModelRenderer }) {
  const [model, setModel] = useState<Model | null>(null);
  const [part, setPart] = useState<ModelPart>({
    id: "",
    position: { x: 0, y: 0, z: 0 },
    rotation: { x: 0, y: 0, z: 0 },
    parent: ''
  });
  const [partIndex, setPartIndex] = useState<number>(-1);
  const [rendered, setRendered] = useState(new THREE.Group());
  const [parents, setParents] = useState(['']);
  useEffect(() => {
    // Effect logic here
    const modelLoaded = (m?: THREE.Object3D) => {
      const model = mr.loader.model;
      setModel(model);
      if (mr.loader.robot) {
        const jointNames = [];
        for (const joint in mr.loader.robot.joints) {
          jointNames.push(joint);
        }
        setParents(jointNames);
      }
    };
    modelLoaded(mr.loader.modelObj);
    mr.loader.onEvent(MLEvents.ModelLoaded, modelLoaded);
    const objectClicked = (object: THREE.Object3D, point: THREE.Vector3) => {
      const loc = point.clone();
      setPartIndex(-1);
      setPart({
        ...part,
        position: { x: loc.x, y: loc.y, z: loc.z },
        rotation: { x: 0, y: 0, z: 0 }
      });
    };
    mr.onEvent(MREvents.ObjectClicked, objectClicked);
    return () => {
      rendered.removeFromParent();
      mr.loader.removeListener(MLEvents.ModelLoaded, modelLoaded);
      mr.removeListener(MREvents.ObjectClicked, objectClicked);
    };
  }, [mr]);

  useEffect(() => {
    rendered.removeFromParent();
    if (!part.parent) {
      mr.scene.add(rendered);
    } else if (mr.loader.robot) {
      const parent = mr.loader.robot.joints[part.parent];
      if (parent) {
        parent.add(rendered);
      } else {
        mr.scene.add(rendered);
      }
    }
    if (rendered.children.length === 0) {
      const geom = new THREE.SphereGeometry(0.05, 16, 16);
      const mat = new THREE.MeshBasicMaterial({
        color: 0xff0000
      });
      const mesh = new THREE.Mesh(geom, mat);
      const axes = new THREE.AxesHelper(0.25);
      rendered.add(mesh);
      rendered.add(axes);
    }
    let index = partIndex;
    if (index === -1) {
      //change material color to blue
      (rendered.children[0] as THREE.Mesh).material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0.5,
        color: 0x0000ff
      });
    } else {
      (rendered.children[0] as THREE.Mesh).material = new THREE.MeshBasicMaterial({
        transparent: true,
        opacity: 0.5,
        color: 0xff0000
      });
    }
    copyVec3(part.position, rendered.position);
    copyVec3(part.rotation, rendered.rotation);
  }, [part, partIndex]);

  if (!model) return null;

  const renderPart = (index?: number) => {
  };


  const mixer = (update: Partial<Model>) => {
    const m = mr.loader.model;
    Object.assign(m, update);
    setModel({ ...m });
  };

  const addPart = async () => {
    setPartIndex(model.parts.length);
    mixer({ parts: [...model.parts, part] });
  };

  const savePart = async () => {
    mixer({ parts: model.parts.map((p, i) => i === partIndex ? part : p) });
  };

  const updatePart = (update: Partial<ModelPart>) => {
    if (update.parent !== undefined && update.parent !== part.parent) {
      const obj = rendered.children[0];
      const wObj = obj.getWorldPosition(new THREE.Vector3());
      if (update.parent === '') {
        update.position = { x: wObj.x, y: wObj.y, z: wObj.z };
      } else {
        const parent = mr.loader.robot!.joints[update.parent];
        if (parent) {
          const wParent = parent.getWorldPosition(new THREE.Vector3());
          const relative = wObj.sub(wParent);
          update.position = { x: relative.x, y: relative.y, z: relative.z };
        }
      }
    }
    setPart(prev => ({ ...prev, ...update }));
  };

  return (
    <Box>
      <List>
        {model.parts.map((part, index) => (
          <ListItem key={part.id} disablePadding dense>
            <ListItemButton onClick={() => {
              setPart(part);
              setPartIndex(index);
              renderPart();
            }}>
              <ListItemText primary={part.id} />
            </ListItemButton>
            <ListItemIcon sx={{ minWidth: "0px" }}>
              <IconButton size="small" onClick={() => {
                mixer({ parts: model.parts.filter(p => p.id !== part.id) });
              }}>
                <Delete />
              </IconButton>
            </ListItemIcon>
          </ListItem>
        ))}
      </List>

      <PropValueLine label='id' value={
        <TextField size='small' variant="standard" value={part.id} onChange={(e) => setPart({ ...part, id: e.target.value })} />
      }
      />
      <PropValueLine label='Position' value={
        <Vec3Editor value={part.position} onChange={(v) => updatePart({ position: v })} />
      } />
      <PropValueLine label='Rotation' value={
        <Vec3Editor convertToDegrees value={part.rotation} onChange={(v) => updatePart({ rotation: v })} />
      } />
      <PropValueLine label='Parent' value={
        <FlexContainer>
          <TextField size="small" value={part.parent || ''} onChange={(e) => updatePart({ parent: e.target.value })} select variant="standard">
            <MenuItem value=''>None</MenuItem>
            {parents.map(parent => <MenuItem key={parent} value={parent}>{parent}</MenuItem>)}
          </TextField>
          <IconButton onClick={() => {
            if (!part.parent || !mr.loader.robot) return;
            const obj = rendered.children[0];
            const parent = mr.loader.robot!.joints[part.parent];
            const wObj = obj.getWorldPosition(new THREE.Vector3());
            const wParent = parent.getWorldPosition(new THREE.Vector3());
            const relative = wObj.sub(wParent);

            console.log("Relative position:", relative.x, relative.y, relative.z);
          }} size='small'>
            <Copy />
          </IconButton>
        </FlexContainer>
      } />

      <Box sx={{ marginTop: 2 }}>
        {partIndex === -1 ? (
          <Button onClick={addPart} color='secondary'>
            Add Part
          </Button>
        ) : (
          <Button onClick={savePart} color='secondary'>
            Save Part
          </Button>
        )}
      </Box>
    </Box>
  );
}
